import { useForm, useFieldArray } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { MoreHorizontal, Plus, Save, Share2, Trash2, Upload } from 'lucide-react';
import {
  createMatchingQuestion, createObjectiveQuestion, createMultipleSelectQuestion, createTheoryQuestion,
  updateMatchingQuestion, updateObjectiveQuestion, updateMultipleSelectQuestion, updateTheoryQuestion,
  bulkDeleteQuestions
} from '../services/quizService';
import { toast } from 'sonner';
import Button from './ui/Button';
import useRemoveQuestion from '../hooks/useRemoveQuestion';
import QuestionFileUploadModal from './QuestionFileUpload';
import QuestionSidebar from '../pages/Admin/Quiz/components/QuestionSidebar';
import ShareQuizModal from './modals/ShareQuizModal';

const QuestionManagementForm = ({
  parentType, // 'quiz' or 'question_bank'
  parentId,
  initialQuestions = [],
  topicId = null,
}) => {
  const [errors, setErrors] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const { deleteState, handleRemoveQuestion } = useRemoveQuestion({ parentType, parentId })
  const { register, control, handleSubmit, reset, watch, formState: { isSubmitting } } = useForm({
    defaultValues: { questions: [] },
  });
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'questions',
  });

  // Populate form fields with questions when data loads
  useEffect(() => {
    reset({ questions: initialQuestions.map(question => ({ ...question, isNew: false })) });
  }, [parentId, parentType]);

  // Helper to build parent object
  const getParentObj = () =>
    parentType === 'quiz'
      ? { quizId: parentId }
      : { questionBankId: parentId };

  // Handle importing questions from sidebar
  const handleImportQuestions = (importedQuestions) => {
    importedQuestions.forEach((question) => {
      append({ ...question, isNew: true })
    })
  }

  const handleTypeChange = (index, type) => {
    const currentQuestion = watch(`questions.${index}`);
    if (!currentQuestion.isNew) {
      toast.error("Question type can't be updated, because it's already saved");
      type = currentQuestion.question_type;
    }
    update(index, { ...currentQuestion, question_type: type });
  };

  const handleAddOption = (index) => {
    const currentQuestion = watch(`questions.${index}`);
    update(index, {
      ...currentQuestion,
      options: [...(currentQuestion.options || []), { text: '', is_correct: false }],
    });
  };

  const handleRemoveOption = (index, optIndex) => {
    const currentQuestion = watch(`questions.${index}`);
    update(index, {
      ...currentQuestion,
      options: currentQuestion.options.filter((_, i) => i !== optIndex),
    });
  };

  const handleRemoveMatchingPair = (index, pairIndex) => {
    const currentQuestion = watch(`questions.${index}`);
    update(index, {
      ...currentQuestion,
      matching_pair: currentQuestion.matching_pair.filter((_, i) => i !== pairIndex),
    });
  };

  const handleAddMatchingPair = (index) => {
    const current = watch(`questions.${index}`);
    update(index, {
      ...current,
      matching_pair: [...(current.matching_pair || []), { left: '', right: '' }],
    });
  };

  const handleDeleteQuestion = (index) => {
    handleRemoveQuestion(index, watch(`questions.${index}`), remove)
    if (errors[index]) {
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[index];
        return newErrors;
      });
    }
  }

  const handleDeleteAll = async () => {
    if (!window.confirm("Are you sure you want to delete all questions? This cannot be undone.")) return;
    try {
      const parentObj = getParentObj();
      await bulkDeleteQuestions(parentObj);
      reset({ questions: [] });
      toast.success('All questions deleted!');
    } catch (error) {
      toast.error('Failed to delete all questions');
    }
  };

  const onSubmit = async (data) => {
    const newErrors = {};
    try {
      for (const [index, question] of data.questions.entries()) {
        try {
          let response;
          const questionData = { ...question, order: index };
          const parentObj = getParentObj();

          if (!question.isNew) {
            if (question.question_type === 'theory') {
              response = await updateTheoryQuestion(question.id, questionData, parentObj);
            } else if (question.question_type === 'objective') {
              response = await updateObjectiveQuestion(question.id, questionData, parentObj);
            } else if (question.question_type === 'multiple_select') {
              response = await updateMultipleSelectQuestion(question.id, questionData, parentObj);
            } else if (question.question_type === 'matching') {
              response = await updateMatchingQuestion(question.id, questionData, parentObj);
            }
          } else {
            if (question.question_type === 'theory') {
              response = await createTheoryQuestion(questionData, parentObj);
            } else if (question.question_type === 'objective') {
              response = await createObjectiveQuestion(questionData, parentObj);
            } else if (question.question_type === 'multiple_select') {
              response = await createMultipleSelectQuestion(questionData, parentObj);
            } else if (question.question_type === 'matching') {
              response = await createMatchingQuestion(questionData, parentObj);
            }
          }
          update(index, { ...response, isNew: false });
        } catch (error) {
          console.error(`Error saving question ${index + 1}:`, error.response?.data?.detail || error.message);
          newErrors[index] = { message: error.response?.data?.detail || error.response?.data?.non_field_errors || error.response?.data?.question?.non_field_errors || JSON.stringify(error.response?.data) || 'Please check your question.' };
        }
      }
      setErrors(newErrors);
      if (Object.keys(newErrors).length === 0) {
        toast.success('Questions saved successfully');
      } else {
        toast.error('Some questions could not be saved. Please check the errors.');
      }
    } catch (error) {
      toast.error('Failed to save questions');
    }
  };

  // ...render the form, same as your current form, using fields, append, remove, update, etc...

  return (
    <div className="flex flex-col gap-2 md:flex-row md:gap-6 mt-6">
      {/* Main Form */}
      <div className="flex-1 relative">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {fields.map((item, index) => (
            <div key={item.id} className="card card-body bg-base-100">
              <div className="flex justify-between items-center mb-4">
                <input
                  type="text"
                  placeholder="Question"
                  {...register(`questions.${index}.text`)}
                  className="input input-bordered w-full"
                />
                <select
                  {...register(`questions.${index}.question_type`)}
                  className="select select-bordered ml-4"
                  onChange={(e) => handleTypeChange(index, e.target.value)}
                >
                  <option value="objective">Multiple Choice</option>
                  <option value="theory">Theory</option>
                  <option value="matching">Matching</option>
                  <option value="multiple_select">Multiple Select</option>
                </select>
              </div>
              {/* Image Upload */}
              <div className="my-2">
                <div className="border border-dashed border-primary/40 rounded-lg p-4 flex flex-col items-center bg-base-100">
                  <label className="font-medium mb-2 text-primary">Upload Image (optional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        const current = watch(`questions.${index}`);
                        update(index, { ...current, image: reader.result });
                      };
                      reader.readAsDataURL(file);
                    }}
                    className="file-input file-input-bordered file-input-sm w-full max-w-xs"
                  />
                  {item.image && (
                    <div className="mt-3 flex flex-col items-center">
                      <img src={item.image} alt="Question" className="max-h-32 rounded border mb-2" />
                      <button
                        type="button"
                        className="btn btn-xs btn-error ml-2 text-white"
                        onClick={() => {
                          const current = watch(`questions.${index}`);
                          update(index, { ...current, image: "" });
                        }}
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {watch(`questions.${index}.question_type`) === 'objective' && (
                <div className="space-y-2">
                  {item?.options && item.options.map((option, optIndex) => (
                    <div key={optIndex} className="flex flex-col gap-1 mb-2 border rounded-lg p-2 bg-base-50">
                      <div className="flex items-center gap-2">
                        {/* Radio or Checkbox */}
                        {watch(`questions.${index}.question_type`) === 'objective' ? (
                          <input
                            type="radio"
                            name={`questions.${index}.correctOption`}
                            checked={option.is_correct}
                            onChange={() => update(index, {
                              ...watch(`questions.${index}`),
                              options: watch(`questions.${index}.options`).map((opt, i) => ({
                                ...opt,
                                is_correct: i === optIndex,
                              })),
                            })}
                            className="radio radio-primary"
                          />
                        ) : (
                          <input
                            type="checkbox"
                            checked={option.is_correct}
                            onChange={() => {
                              update(index, {
                                ...watch(`questions.${index}`),
                                options: watch(`questions.${index}.options`).map((opt, i) =>
                                  i === optIndex ? { ...opt, is_correct: !opt.is_correct } : opt
                                ),
                              });
                            }}
                            className="checkbox checkbox-primary"
                          />
                        )}
                        {/* Option Text */}
                        <input
                          type="text"
                          placeholder={`Option ${optIndex + 1}`}
                          {...register(`questions.${index}.options.${optIndex}.text`)}
                          className="input input-bordered w-full"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-error text-white"
                          onClick={() => handleRemoveOption(index, optIndex)}
                        >
                          Remove
                        </button>
                      </div>
                      {/* Collapsible Explanation */}
                      <details className="ml-8">
                        <summary className="cursor-pointer text-sm text-primary">Add explanation for this option</summary>
                        <textarea
                          placeholder="Explanation (optional)"
                          {...register(`questions.${index}.options.${optIndex}.explanation`)}
                          className="textarea textarea-bordered w-full mt-2"
                          rows={2}
                        />
                      </details>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={() => handleAddOption(index)}
                  >
                    Add Option
                  </button>
                </div>
              )}
              {watch(`questions.${index}.question_type`) === 'multiple_select' && (
                <div className="space-y-2">
                  {item?.options && item.options.map((option, optIndex) => (
                    <div key={optIndex} className="flex flex-col gap-1 mb-2 border rounded-lg p-2 bg-base-50">
                      <div key={optIndex} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={option.is_correct}
                          onChange={() => {
                            // Toggle is_correct for this option
                            update(index, {
                              ...watch(`questions.${index}`),
                              options: watch(`questions.${index}.options`).map((opt, i) =>
                                i === optIndex ? { ...opt, is_correct: !opt.is_correct } : opt
                              ),
                            });
                          }}
                          className="checkbox checkbox-primary"
                        />
                        <input
                          type="text"
                          placeholder={`Option ${optIndex + 1}`}
                          {...register(`questions.${index}.options.${optIndex}.text`)}
                          className="input input-bordered w-full"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-error text-white"
                          onClick={() => handleRemoveOption(index, optIndex)}
                        >
                          Remove
                        </button>
                      </div>
                      {/* Collapsible Explanation */}
                      <details className="ml-8">
                        <summary className="cursor-pointer text-sm text-primary">Add explanation for this option</summary>
                        <textarea
                          placeholder="Explanation (optional)"
                          {...register(`questions.${index}.options.${optIndex}.explanation`)}
                          className="textarea textarea-bordered w-full mt-2"
                          rows={2}
                        />
                      </details>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={() => handleAddOption(index)}
                  >
                    Add Option
                  </button>
                </div>
              )}
              {watch(`questions.${index}.question_type`) === 'matching' && (
                <div className="space-y-2">
                  {(item.matching_pair || []).map((pair, pairIdx) => (
                    <div key={pair.id || pairIdx} className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Left"
                        {...register(`questions.${index}.matching_pair.${pairIdx}.left`)}
                        className="input input-bordered w-full"
                      />
                      <span className="mx-2">=</span>
                      <input
                        type="text"
                        placeholder="Right"
                        {...register(`questions.${index}.matching_pair.${pairIdx}.right`)}
                        className="input input-bordered w-full"
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-error text-white"
                        onClick={() => { handleRemoveMatchingPair(index, pairIdx) }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={() => { handleAddMatchingPair(index) }}
                  >
                    Add Pair
                  </button>
                </div>
              )}
              {watch(`questions.${index}.question_type`) === 'theory' && (
                <textarea
                  placeholder="Answer"
                  {...register(`questions.${index}.answer`)}
                  className="textarea textarea-bordered w-full"
                />
              )}
              {errors[index] && (
                <div className="text-red-500 mt-2">
                  {Object.entries(errors[index]).map(([key, messages]) => (
                    <div key={key}>
                      <strong>{key}:</strong> {Array.isArray(messages) ? messages.join(', ') : messages}
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="btn btn-sm btn-error text-white mt-4"
                onClick={() => handleDeleteQuestion(index)}
              >
                {deleteState.isDeleting && deleteState.deletingQuestionIndex === index
                  ? 'Deleting...'
                  : 'Remove Question'}
              </button>
            </div>
          ))}

          {/* Floating Add Question button after the last question */}
          <div className="sticky bottom-4 flex justify-end z-50">
            <button
              type="button"
              className="btn btn-primary btn-circle btn-lg shadow-lg"
              onClick={() =>
                append({ question_type: "objective", text: "", options: [{ text: "", is_correct: false }], isNew: true })
              }
              aria-label="Add New Question"
            >
              <Plus size={24} strokeWidth={2} />
            </button>
          </div>
          
          {/* More Options */}
          <div className="flex flex-wrap justify-between items-center mt-6 px-2 gap-2">
            {/* Left Side - More Dropdown */}
            <div className="dropdown dropdown-end">
              <label tabIndex={0} className="btn btn-outline m-1 flex items-center gap-2">
                <MoreHorizontal className="w-4 h-4" />
                More
              </label>
              <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-56">
                <li>
                  <a onClick={handleDeleteAll}>
                    <Trash2 className="w-4 h-4 text-error" />
                    <span className="text-error">Delete All</span>
                  </a>
                </li>
                <li>
                  <a onClick={() => setShowUploadModal(true)}>
                    <Upload className="w-4 h-4 text-accent" />
                    Upload Questions
                  </a>
                </li>
                <li>
                  <a onClick={() => setShowShareModal(true)}>
                    <Share2 className="w-4 h-4 text-success" />
                    Share Quiz
                  </a>
                </li>
              </ul>
            </div>

            {/* Right Side - Primary Buttons */}
            <div className="flex gap-2">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() =>
                  append({
                    question_type: "objective",
                    text: "",
                    options: [{ text: "", is_correct: false }],
                    isNew: true,
                  })
                }
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Question
              </button>
              <Button isLoading={isSubmitting} type="submit" className="btn btn-primary flex items-center gap-2">
                <Save className="w-4 h-4" />
                Save Quiz
              </Button>
            </div>
          </div>

        </form>
      </div>

      {topicId &&
        <div className="-order-1 md:order-2">
          <QuestionSidebar topicId={topicId} onImportQuestions={handleImportQuestions} />
        </div>}
      
      <QuestionFileUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadSuccess={(uploadedQuestions) => {
          toast.success(`${uploadedQuestions.length} questions imported!`);
          uploadedQuestions.forEach((q) => append({ ...q, isNew: true }));
        }}
      />

      {/* Share Modal */}
      <ShareQuizModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        quizId={parentType === 'quiz' ? parentId : null}
      />
    </div>
  );
};

export default QuestionManagementForm;